import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, Zap } from 'lucide-react';
import { useSubscription } from '@/lib/hooks/useSubscription';

interface TrialLimitReachedProps {
  onClose?: () => void;
}

export default function TrialLimitReached({ onClose }: TrialLimitReachedProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { trialDaysRemaining, mutate } = useSubscription();

  const handleUpgradeNow = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch('/api/stripe/upgrade-early', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to upgrade');
      }

      // Refresh subscription state
      await mutate();
      onClose?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
      setLoading(false);
    }
  };

  return (
    <Card className="p-6 border-warning-border bg-warning-surface">
      <div className="flex items-start gap-4">
        <div className="p-2 bg-warning-surface-strong rounded-full">
          <Zap className="h-5 w-5 text-warning-foreground" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-content-heading">
            Trial Enrichment Limit Reached
          </h3>
          <p className="text-sm text-content-secondary mt-1">
            You&apos;ve used all 10 trial enrichments. Upgrade now to unlock
            1,000+ enrichments per month, or wait {trialDaysRemaining} days
            until your trial ends and billing begins.
          </p>

          {error && (
            <p className="text-sm text-danger-foreground mt-2">
              {error}
            </p>
          )}

          <div className="flex gap-3 mt-4">
            <Button onClick={handleUpgradeNow} disabled={loading} size="sm">
              {loading ? (
                <>
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  Upgrading...
                </>
              ) : (
                'Upgrade Now'
              )}
            </Button>
            {onClose && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                disabled={loading}
              >
                Remind Me Later
              </Button>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
